import React, { createContext, useCallback, useState } from 'react';
import _isArray from 'lodash/isArray';
import _get from 'lodash/get';

import { PageErrorUi } from 'components';
import Loader from './Loader';
import { useFetch, useMutation, useSegment } from 'utils/hooks';
import { sortRefundMethods } from 'utils/methods';
import { updateRefundMethods } from './utils';
import { getRefundMethodChoices, getRefundMethodSlugs } from 'utils/DICTIONARY';

/**
 * @type {React.Context<{
 *  refundMethods: any[],
 *  refundMethodNameMap: Record<string, string>,
 *  refundMethodSlugMap: Record<string, string>,
 *  onRefundMethodUpdated: (data: any) => void,
 *  onSetAsDefault: (method: any) => void,
 *  isSettingDefault: boolean,
 *  onReload: () => void
 * }}
 */
const RefundMethodsListContext = createContext();

export const useRefundMethodsList = () => {
  const context = React.useContext(RefundMethodsListContext);
  if (!context) {
    throw new Error(
      'useRefundMethodsList must be used within a RefundMethodsListProvider'
    );
  }
  return context;
};

export function RefundMethodsListProvider({ children = null }) {
  const { track } = useSegment();
  const [refundMethods, setRefundMethods] = useState([]);
  const [refundMethodNameMap] = useState(() => getRefundMethodChoices());
  const [refundMethodSlugMap] = useState(() => getRefundMethodSlugs());

  const { isLoading, isError, fetchData: onReload } = useFetch(
    'configure.refundMethods.root',
    {
      onTransform: data => (_isArray(data) ? data : []),
      onSuccess: data => setRefundMethods(sortRefundMethods(data))
    }
  );

  const { mutate, isProcessing: isSettingDefault } = useMutation(
    'configure.refundMethods.root',
    'PUT',
    {
      successMessage: 'Successfully set as default refund method.',
      errorMessage: 'Unable to set as default at the moment. Please try later!',
      onSuccess: data => {
        const methodId = _get(data, 'id', '');
        const paymentMethod = _get(data, 'payment_method', '');
        track('FTU Wizard Refund Method Set As Default', {
          method_id: methodId,
          payment_method: paymentMethod
        });
        onReload();
      }
    }
  );

  const onSetAsDefault = useCallback(
    method => {
      if (!method || !method.id) return;
      mutate({ routeParam: method.id, ...method, is_default: true });
    },
    [mutate]
  );

  const onRefundMethodUpdated = useCallback((data = null) => {
    if (!data) return;
    setRefundMethods(prevMethods => updateRefundMethods(prevMethods, data));
  }, []);

  return (
    <RefundMethodsListContext.Provider
      value={{
        refundMethods,
        refundMethodNameMap,
        refundMethodSlugMap,
        onRefundMethodUpdated,
        onSetAsDefault,
        isSettingDefault,
        onReload
      }}
    >
      <PageErrorUi
        isError={isError}
        onReload={onReload}
        msg="Unable to load the refund methods at the moment!"
      >
        <Loader isLoading={isLoading}>{children}</Loader>
      </PageErrorUi>
    </RefundMethodsListContext.Provider>
  );
}
